import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import isEqual from 'react-fast-compare';
import { pageContext, fetchServerTime, convertEpochToLocalDateString, get } from 'ctm-util-functions';
import { experimentIdSelector, splitTrafficIdSelector } from 'ctm-selectors';

import Immutable from 'immutable';
import qsParser from 'querystring';
import { onRouteChange } from './route_utils';

// eslint-disable-next-line valid-jsdoc
/**
 * @desc Base page wrapper component.
 *       This is used as an entry point to each page that we have a route for
 *       The routes 'render' this component which will use the schema and
 *       builders passed to it by the routes to generate the actual page that
 *       should be rendered.
 */
class _PageWrapper extends Component {

    constructor(props) {
        super(props);
        this.state = {
            isRedirecting: false,
            isRouteChanged: false
        };
    }

    /**
     * When this mounts (only happens once per journey) set the browserHistory on app state and call onRouteChange to
     * handle any required redirects etc
     */
     // eslint-disable-next-line camelcase
     UNSAFE_componentWillMount = () => {
         const { history } = this.props;
         pageContext.setProp('browserHistory', history);
         const { isRedirecting, isRouteChanged } = onRouteChange(this.props);
         this.setState({ isRedirecting, isRouteChanged });
     };

    /**
     * Called when the component is mounted
     */
    componentDidMount = () => {
        this.setServerTime();
    };

    /**
     * Determine if the component should re-render based on the nextProps
     * We have to use this as we're mapping objects onto props and the default shallow equal
     * doesn't work in this case and always causes a re-render on state changes.
     * Using react-fast-compare from https://github.com/FormidableLabs/react-fast-compare
     * @param  {Object} nextProps  The incoming set of props
     * @param  {Object} nextState  The incoming state
     * @returns {Boolean}           true if the component should re-render
     */
    shouldComponentUpdate = (nextProps, nextState) => {
        const { location, pageSchema } = this.props;
        let updateLayoutOnBreakpointChange = nextProps.location.pathname !== location.pathname;

        /**
         * Check if the pageWrapper Component should be updated on a breakpoint change.
         * @todo This might be moved to the FlexBox Component but it would need to receive
         * the pageState/breakpoint property or be connected to the browser state from The Redux store.
         */
        if (pageSchema && pageSchema.updateLayoutOnBreakpointChange) {
            updateLayoutOnBreakpointChange = true;
        }

        // @todo: really only need to check this.props.location and breakpoint here
        return !isEqual(nextProps, this.props) && updateLayoutOnBreakpointChange;
    };

    /**
     * Call onRouteChange() with the incoming props when the location has changed so that we can handle any conditional
     * step redirects etc
     * @param {Object} nextProps - The incoming set of props
     */
     // eslint-disable-next-line camelcase
     UNSAFE_componentWillReceiveProps = (nextProps) => {
         const { location } = this.props;
         if (nextProps.location.pathname !== location.pathname) {
             const { isRedirecting, isRouteChanged } = onRouteChange(nextProps);
             this.setState({ isRedirecting, isRouteChanged });
             this.setServerTime();
         }
     };

    /**
     * Set serverTime in the store from server_time api express endpoint
     */
    setServerTime = () => {
        const { dispatch } = this.props;
        fetchServerTime((response) => {
            let serverTime = convertEpochToLocalDateString(response);
            if (pageContext.inDevMode()) {
                serverTime = this.getServerTimeQueryString(serverTime);
            }
            dispatch({ type: 'SET_PAGE_VALUE', identifier: 'serverTime', value: serverTime });
        });
    };

    /**
     * Retrives the serverTime from Query Params if supplied
     * @param {String} serverTime The Time from server
     * @returns {String} The Date string from serverTime Query Params or default from serverTime
     */
    getServerTimeQueryString = (serverTime) => {
        const search = get(this.props, 'location.search', '');
        const queryParams = Immutable.Map(qsParser.parse(search.substr(1)) || {});
        return queryParams.has('server_time') ? convertEpochToLocalDateString(queryParams.getIn(['server_time'])) : serverTime;
    };

    getPageSchema = () => {
        const { pageSchema, splitTrafficId, experimentId } = this.props;
        if (pageSchema && pageSchema.whenAbTesting
                && (splitTrafficId === pageSchema.whenAbTesting.splitTrafficId || experimentId === pageSchema.whenAbTesting.experimentId)) {
            return {
                ...pageSchema,
                components: pageSchema.whenAbTesting.componentsReplacement
            };
        }
        return pageSchema;
    };

    /**
     * Call the page builder with the props passed down from the route
     * @returns {[type]} [description]
     */
    render = () => {
        const {
            pageState,
            isSSR,
            location
        } = this.props;
        const { isRedirecting, isRouteChanged } = this.state;
        const builders = pageContext.getProps().builders;

        const pageSchema = this.getPageSchema();
        return (
            builders.page(
                pageState, // @todo, this will get fixed in a later ticket when we can tackle the state mapping issues
                pageSchema,
                isSSR,
                location,
                isRedirecting,
                isRouteChanged
            )
        );
    };

}
_PageWrapper.propTypes = {
    pageState: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    pageSchema: PropTypes.object.isRequired,
    isSSR: PropTypes.bool.isRequired,
    // Passed in from the route
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    splitTrafficId: PropTypes.string,
    experimentId: PropTypes.string
};

export const mapStateToProps = (state, props) => {
    const splitTrafficId = splitTrafficIdSelector(state);
    const experimentId = experimentIdSelector(state);
    /**
     * While we are wrapping these properties in a pageState object,
     * we need to do a deep equals in shouldComponentUpdate as it does not do it by default.
     */
    return {
        pageState: {
            breakpoint: state.get('browser').mediaType,
            path: props.pageSchema.path,
            pageName: props.pageSchema.name,
            serverConfig: state.get('serverConfig')
        },
        splitTrafficId,
        experimentId
    };
};

const PageWrapper = connect(mapStateToProps)(_PageWrapper);
export {  _PageWrapper, PageWrapper };
